import React, { useMemo } from "react";
import {
  AlertTriangle,
  Banknote,
  Car,
  ChevronRight,
  Clock,
  Package,
  Receipt,
  TrendingUp,
  Wallet,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { totalOS } from "./os";
import { Btn, Pill, Placa, Stat, Vazio } from "../ui/componentes";
import { HOJE, ST, atrasado, brl, dias } from "../ui/formato";

/* ══════════════════════════════════════════════════════════════════
   PAINEL
   ══════════════════════════════════════════════════════════════════ */
function Painel({ db, ir, usuario, abrirOS }) {
  const cli = (id) => db.clientes.find((c) => c.id === id);
  const vei = (id) => db.veiculos.find((v) => v.id === id);
  const totalOS = (o) => o.itens.reduce((s, i) => s + i.qtd * i.valor, 0) - (o.desconto || 0);

  const naOficina = db.os.filter((o) => ["aprovada", "execucao", "finalizada"].includes(o.status));
  const aguardando = db.os.filter((o) => o.status === "aguardando");
  const baixas = db.pecas.filter((p) => p.qtd <= p.min);
  const receber = db.fin.filter((f) => f.tipo === "receber" && !f.pago);
  const vencido = receber.filter((f) => atrasado(f.venc));
  const recebidoMes = db.fin.filter((f) => f.tipo === "receber" && f.pago && f.pago >= dias(-31)).reduce((s, f) => s + f.valor, 0);
  /**
   * Faturamento dos últimos seis meses, das OS fechadas.
   *
   * Era o array FATURAMENTO, fixo desde o protótipo. A oficina abria o sistema
   * no primeiro dia, sem nenhum registro, e via R$ 17 mil de faturamento que
   * nunca existiu — no indicador e no gráfico. Número inventado na primeira
   * tela custa a confiança no sistema inteiro.
   *
   * O enum é ('peca','servico','terceirizado','taxa'): só `peca` conta como
   * peça; o resto é serviço prestado.
   */
  const serieFaturamento = useMemo(() => {
    const NOMES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    const base = HOJE();
    const meses = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
      meses.push({
        chave: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        mes: NOMES[d.getMonth()], pecas: 0, servicos: 0,
      });
    }
    const porChave = Object.fromEntries(meses.map((m) => [m.chave, m]));
    for (const o of db.os) {
      if (!["finalizada", "entregue"].includes(o.status)) continue;
      const alvo = porChave[(o.abertura || "").slice(0, 7)];
      if (!alvo) continue;
      for (const i of o.itens ?? []) {
        const v = (i.qtd || 0) * (i.valor || 0);
        if (i.tipo === "peca") alvo.pecas += v; else alvo.servicos += v;
      }
    }
    return meses;
  }, [db.os]);

  const faturamentoMes = serieFaturamento[5].pecas + serieFaturamento[5].servicos;
  const faturamentoAnterior = serieFaturamento[4].pecas + serieFaturamento[4].servicos;
  // Sem mês anterior não há comparação honesta a fazer — o Stat esconde o delta.
  const variacao = faturamentoAnterior
    ? Math.round(((faturamentoMes - faturamentoAnterior) / faturamentoAnterior) * 100)
    : undefined;
  const osFechadas = db.os.filter((o) => ["finalizada", "entregue"].includes(o.status));
  const ticket = osFechadas.length ? osFechadas.reduce((s, o) => s + totalOS(o), 0) / osFechadas.length : 0;

  const porStatus = Object.keys(ST).map((k) => ({ nome: ST[k].l, qtd: db.os.filter((o) => o.status === k).length })).filter((x) => x.qtd > 0);
  const CORES = ["#8D9AA6", "#E2620C", "#4FAFD1", "#A8630A", "#137A56", "#697987", "#BC3227"];

  return (
    <div className="grid gap-4">
      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))" }}>
        <Stat label={`Faturamento em ${serieFaturamento[5].mes.toLowerCase()}`} value={brl(faturamentoMes)} icon={TrendingUp} delta={variacao} sub={faturamentoAnterior ? `vs. ${serieFaturamento[4].mes.toLowerCase()}` : "primeiro mês"} />
        <Stat label="Veículos na oficina" value={naOficina.length} icon={Car} sub={`${aguardando.length} aguardando aprovação`} />
        <Stat label="Ticket médio por OS" value={brl(ticket)} icon={Receipt} sub={`${osFechadas.length} OS fechada(s)`} />
        <Stat label="A receber" value={brl(receber.reduce((s, f) => s + f.valor, 0))} tone={vencido.length ? "signal" : "ink"} icon={Wallet}
          sub={vencido.length ? `${vencido.length} título(s) vencido(s)` : "nenhum vencido"} />
      </div>

      {/* o que precisa de você agora */}
      {(baixas.length > 0 || aguardando.length > 0 || vencido.length > 0) && (
        <div className="card p-4" style={{ borderLeft: "3px solid var(--signal)" }}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={15} className="t-signal" />
            <span className="dsp" style={{ fontSize: 15 }}>Precisa de você agora</span>
          </div>
          <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))" }}>
            {baixas.length > 0 && (
              <button onClick={() => ir("estoque")} className="flex items-center gap-3 px-3 py-2.5 text-left" style={{ background: "var(--signal-l)", border: "1px solid #F6D3B6", borderRadius: "var(--r)", cursor: "pointer" }}>
                <Package size={17} className="t-signal flex-none" />
                <span className="flex-1"><span className="block text-sm font-semibold">{baixas.length} peças abaixo do mínimo</span>
                  <span className="block text-xs t-mut">{baixas.slice(0, 2).map((p) => p.nome).join(", ")}{baixas.length > 2 ? "…" : ""}</span></span>
                <ChevronRight size={15} className="t-mut2" />
              </button>
            )}
            {aguardando.length > 0 && (
              <button onClick={() => ir("os")} className="flex items-center gap-3 px-3 py-2.5 text-left" style={{ background: "var(--gulf-l)", border: "1px solid #BEE0EC", borderRadius: "var(--r)", cursor: "pointer" }}>
                <Clock size={17} className="t-gulf flex-none" />
                <span className="flex-1"><span className="block text-sm font-semibold">{aguardando.length} orçamento(s) esperando o cliente</span>
                  <span className="block text-xs t-mut">Reenviar pelo WhatsApp ou ligar</span></span>
                <ChevronRight size={15} className="t-mut2" />
              </button>
            )}
            {vencido.length > 0 && (
              <button onClick={() => ir("financeiro")} className="flex items-center gap-3 px-3 py-2.5 text-left" style={{ background: "var(--crit-l)", border: "1px solid #F1C9C5", borderRadius: "var(--r)", cursor: "pointer" }}>
                <Banknote size={17} className="t-crit flex-none" />
                <span className="flex-1"><span className="block text-sm font-semibold">{brl(vencido.reduce((s, f) => s + f.valor, 0))} vencidos</span>
                  <span className="block text-xs t-mut">{vencido.length} título(s) em atraso</span></span>
                <ChevronRight size={15} className="t-mut2" />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))" }}>
        <div className="card p-4">
          <div className="flex items-baseline justify-between mb-3">
            <span className="dsp" style={{ fontSize: 15 }}>Faturamento por mês</span>
            <span className="text-xs t-mut2">peças × serviços</span>
          </div>
          <ResponsiveContainer width="100%" height={214}>
            <BarChart data={serieFaturamento} margin={{ top: 4, right: 4, left: -14, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#DCE1E6" vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#697987" }} axisLine={{ stroke: "#DCE1E6" }} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#8D9AA6" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip formatter={(v, n) => [brl(v), n === "pecas" ? "Peças" : "Serviços"]} contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #DCE1E6" }} />
              <Bar dataKey="servicos" stackId="a" fill="#4FAFD1" radius={[0, 0, 0, 0]} />
              <Bar dataKey="pecas" stackId="a" fill="#171C22" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card p-4">
          <span className="dsp block mb-3" style={{ fontSize: 15 }}>Ordens de serviço por situação</span>
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="52%" height={200}>
              <PieChart>
                <Pie data={porStatus} dataKey="qtd" nameKey="nome" innerRadius={44} outerRadius={72} paddingAngle={2} stroke="none">
                  {porStatus.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #DCE1E6" }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 grid gap-1.5">
              {porStatus.map((s, i) => (
                <div key={s.nome} className="flex items-center gap-2 text-sm">
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: CORES[i % CORES.length], flex: "none" }} />
                  <span className="flex-1 t-mut">{s.nome}</span>
                  <span className="mono font-semibold">{s.qtd}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ overflow: "hidden" }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--line)" }}>
          <span className="dsp" style={{ fontSize: 15 }}>Na oficina agora</span>
          <Btn variant="g" size="xs" icon={ChevronRight} onClick={() => ir("os")}>Ver todas as OS</Btn>
        </div>
        {naOficina.length === 0 ? (
          <Vazio icon={Car} titulo="Nenhum veículo no pátio" msg="Assim que você abrir uma OS aprovada, o veículo aparece aqui." />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead><tr><th>OS</th><th>Veículo</th><th>Cliente</th><th>Serviço</th><th>Mecânico</th><th>Situação</th><th className="num">Total</th><th></th></tr></thead>
              <tbody>
                {naOficina.map((o) => {
                  // Sem `?.` aqui a tela inteira ia para branco quando o carro
                  // não vinha na lista de veículos — ver `placa` em deOS.
                  const v = vei(o.vei), c = cli(o.cli), m = db.usuarios.find((u) => u.id === o.mec);
                  return (
                    <tr key={o.id} style={{ cursor: "pointer" }} onClick={() => abrirOS(o.id)}>
                      <td className="mono text-sm font-semibold">{o.id}</td>
                      <td><div className="flex items-center gap-2.5"><Placa n={v?.placa ?? o.placa} /><span className="text-sm">{v?.marca ?? o.marca} {v?.modelo ?? o.modelo}</span></div></td>
                      <td className="text-sm">{c?.nome ?? o.clienteNome}</td>
                      <td className="text-sm t-mut" style={{ maxWidth: 230 }}><span className="block truncate">{o.queixa}</span></td>
                      <td className="text-sm">{m?.nome.split(" ")[0]}</td>
                      <td><Pill t={(ST[o.status]?.c ?? "p-mut").replace("p-", "")}>{ST[o.status]?.l ?? o.status}</Pill></td>
                      <td className="num text-sm font-semibold">{brl(totalOS(o))}</td>
                      <td><ChevronRight size={15} className="t-mut2" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export { Painel };
